import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Bell, CheckCheck } from "lucide-react";
import api, { fmtDate } from "./api";
import { PageHeader, EmptyState } from "./components";
import { EASE } from "@/components/Motion";

export default function NotificationsPage() {
  const { refreshUnread } = useOutletContext();
  const [data, setData] = useState(null);

  const load = () => api.get("/notifications").then(({ data }) => setData(data)).catch(() => setData({ items: [], unread: 0 }));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
    refreshUnread().catch(() => {});
  };

  return (
    <div data-testid="notifications-page">
      <PageHeader title="Notifications" sub="Signals from the engine room.">
        {data?.unread > 0 && (
          <button
            onClick={markAll}
            data-testid="mark-all-read-button"
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-xs font-semibold text-goldlight transition-all duration-300 hover:bg-gold hover:text-black"
          >
            <CheckCheck size={14} /> Mark all read
          </button>
        )}
      </PageHeader>

      {data === null ? (
        <div className="flex justify-center py-24">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
        </div>
      ) : data.items.length === 0 ? (
        <EmptyState icon={Bell} title="All quiet" sub="Order updates and replies will land here." testid="notifications-empty" />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="space-y-3"
          data-testid="notifications-list"
        >
          {data.items.map((n) => (
            <div
              key={n.id}
              data-testid={`notification-${n.id}`}
              className={`flex gap-4 rounded-2xl border p-5 transition-colors duration-300 ${
                n.read
                  ? "border-white/5 bg-panel/60"
                  : "border-gold/25 bg-gold/[0.05]"
              }`}
            >
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-white/10" : "bg-gold shadow-[0_0_8px_rgb(var(--gold)_/_0.8)]"}`} />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-sm font-semibold ${n.read ? "text-neutral-300" : "text-neutral-50"}`}>{n.title}</span>
                  {n.kind && (
                    <span className="rounded-full border border-gold/40 px-2 py-0.5 font-mono text-[8px] uppercase tracking-wider text-gold">
                      {n.kind}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm leading-relaxed text-neutral-500">{n.body}</p>
                <div className="mt-2 font-mono text-[10px] tracking-[0.15em] text-neutral-600">{fmtDate(n.created_at)}</div>
              </div>
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
